import { EmailCaptureWithTopic, FeatureType, GetFolderResponse, GetTopicResponse, IStats, StatType } from '../types';
import { LitElement, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';

import { Dialog } from '@material/mwc-dialog';
import { StateController } from '../helpers/state/state-management/state-controller';
import { TextField } from '@material/mwc-textfield';
import accountInfo from '../helpers/state/account-info';
import { detectMobileViewport } from '../helpers/device';
import { escapeCsvValue } from '../helpers/files';
import featuresInfo from '../helpers/state/features-info';
import { getChallenges } from '../helpers/api/challenge-api';
import { getEmailCapturesForTopics } from '../helpers/api/email-capture-api';
import { getMainPage } from '../helpers/page-name';
import { ifDefined } from 'lit/directives/if-defined';
import { numberFormatter } from '../helpers/math';

@customElement('insight-cards')
export class BackpackCards extends LitElement {
    private readonly amplitude_callsite: string = 'Insight Cards';

    @query('#searchText')
    private readonly searchTextElement?: TextField;

    @query('#searchTextMobile')
    private readonly searchTextElementMobile?: TextField;

    @query('#deleteFolderModalComponent')
    deleteFolderModalComponent!: Dialog;

    @property({ type: Array })
    topics: GetTopicResponse[] = [];

    @property({ type: Array })
    folders: GetFolderResponse[] = [];

    @property({ type: Array })
    flattened_emails: EmailCaptureWithTopic[] = [];

    @property({ type: Array })
    challenges: any[] = [];

    @property({ type: Number })
    challengesLength: number = 0;

    @state()
    selectedMyMaps: boolean = false;

    @state()
    selectedBookmarks: boolean = false;

    @state()
    filteredTopics: GetTopicResponse[] = [];

    @state()
    createdTopics: GetTopicResponse[] = [];

    @state()
    bookmarkedTopics: GetTopicResponse[] = [];

    @state()
    topicsToRender: GetTopicResponse[] = [];

    @state()
    statsList: IStats[] = [];

    @state()
    isLoading: boolean = false;

    @property({ type: Array})
    emailWithTopic: EmailCaptureWithTopic[] = [];

    private readonly bindAccountState = new StateController(this, accountInfo);

    async updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('topics')) {
            await this.getEmails();
            this.statsList = this.calculateStats(this.topics);
            this.requestUpdate();
        }

        if (changedProperties.has('statsList')) {
            this.requestUpdate();
        }
    }

    async firstUpdated() {
        await this.startup();
    }

    private async startup() {
        this.statsList = [
            { name: 'Map Views This Month', calculation: StatType.MonthlyViews, value: 0, blocked: false},
            { name: 'Engagements', calculation: StatType.TotalPointClicks, value: 0, blocked: false},
            { name: 'Leads', calculation: StatType.EmailsCaptured, value: 0, blocked: false, action_string: 'Download', action_function: async () => await this.downloadCapturedEmails()},
            { name: 'Places Added', calculation: StatType.TotalMapPoints, value: 0, blocked: false},
            { name: 'Top Trending Map', calculation: StatType.TrendingMap, value: 0, blocked: false},
            // { name: 'Emails Captured', calculation: StatType.EmailsCaptured, value: 0, blocked: false, moreInfoSubscribed: 'Download Emails', moreInfoSubscribedAction: async () => await this.downloadCapturedEmails(), moreInfoNotSubscribed: 'Generate Leads from Your Maps', moreInfoNotSubscribedAction: () => this.openUpgradeDialog(FeatureType.EmailCapture), subscriptionNeed: featuresInfo.emailCaptureEnabled },
            // { name: 'Challenge Registrants', calculation: StatType.ChallengeParticipants, value: 0, blocked: false, moreInfoNotSubscribed: 'Gamify your Maps', moreInfoNotSubscribedAction: () => this.openUpgradeDialog(FeatureType.ChallengePassport), subscriptionNeed: featuresInfo.challengePassportEnabled },
            { name: 'Most Engaged Map', calculation: StatType.MostEngagedMap, value: 0, blocked: false},
            { name: 'Most Viewed Map', calculation: StatType.MostViewedMap, value: 0, blocked: false},
        ];
        this.statsList = this.calculateStats(this.topics);
    }

    private openUpgradeDialog(feature: FeatureType) {
        const event = new CustomEvent('open-upgrade-dialog', {
            detail: {
                feature_type: feature
            }
        });
        this.dispatchEvent(event);
    }

    private calculateStats(topics: GetTopicResponse[]) {
        const filteredTopics = topics.filter((topic) => topic.is_writer);
        this.statsList.forEach((stat) => {
            switch (stat.calculation) {
                case StatType.UserMapsCreated:
                    stat.value = filteredTopics.length;
                    break;
                case StatType.TotalMapPoints:
                    stat.value = this.getTotalMapPoints(topics);
                    break;
                case StatType.EmailsCaptured:
                    stat.value = this.emailWithTopic.length;
                    break;
                case StatType.ChallengeParticipants:
                    stat.value = this.challengesLength;
                    break;
                case StatType.TotalMapViews:
                    stat.value = this.getTotalMapViews(topics);
                    break;
                case StatType.UserPlacesVisited:
                    stat.value = this.getUniquePlaceVisits(topics);
                    break;
                case StatType.TotalPointClicks:
                    stat.value = this.getTotalPointClicks(topics);
                    break;
                case StatType.MonthlyViews:
                    stat.value = this.getTotalMapViewsThirtyDays(topics);
                    break;
                case StatType.TrendingMap:
                    stat.topic = this.getTrendingMap(topics);
                    break;
                case StatType.MostEngagedMap:
                    stat.topic = this.getMostEngagedMap(topics);
                    break;
                case StatType.MostViewedMap:
                    stat.topic = this.getMostViewedMap(topics);
                    break;
                default:
                    break;
            }
        });
        return this.statsList;
    }

    private getTotalMapViews(topics: GetTopicResponse[]) {
        let totalMapViews = 0;
        topics.forEach((topic) => {
            totalMapViews += topic.statistics.approximate_topic_views;
        });
        return totalMapViews;
    }

    private getTotalMapViewsThirtyDays(topics: GetTopicResponse[]) {
        let totalMapViews = 0;
        topics.forEach((topic) => {
            totalMapViews += topic.statistics.approximate_topic_thirty_day_views;
        });
        return totalMapViews;
    }

    private getTotalMapPoints(topics: GetTopicResponse[]) {
        let totalMapPoints = 0;
        topics.forEach((topic) => {
            totalMapPoints += topic.statistics.approximate_point_count;
        });
        return totalMapPoints;
    }

    private getUniquePlaceVisits(topics: GetTopicResponse[]) {
        let uniquePlaceVisits = 0;
        uniquePlaceVisits = accountInfo.account_checkins.size;
        return uniquePlaceVisits;
    }

    private getTotalPointClicks(topics: GetTopicResponse[]) {
        let totalPointClicks = 0;
        topics.forEach((topic) => {
            totalPointClicks += topic.statistics.approximate_point_views;
        });
        return totalPointClicks;
    }

    private getTrendingMap(topics: GetTopicResponse[], minViews: number = 50) {
        const recentTopics = topics.filter((topic) => Date.parse(topic.updated_at) > new Date().getTime() - 365 * 24 * 60 * 60 * 1000);
        let trendingMap = recentTopics[0];
        recentTopics.forEach((topic) => {
            if (topic.statistics.approximate_topic_thirty_day_views > minViews &&
                topic.statistics.approximate_topic_thirty_day_views > trendingMap.statistics.approximate_topic_thirty_day_views) {
                trendingMap = topic;
            }
        });
        return trendingMap;
    }

    private getMostEngagedMap(topics: GetTopicResponse[]) {
        const recentTopics = topics.filter((topic) => Date.parse(topic.updated_at) > new Date().getTime() - 365 * 24 * 60 * 60 * 1000);
        let mostEngagedMap = recentTopics[0];
        recentTopics.forEach((topic) => {
            if (topic.statistics.approximate_point_views > mostEngagedMap.statistics.approximate_point_views) {
                mostEngagedMap = topic;
            }
        });
        return mostEngagedMap;
    }

    private getMostViewedMap(topics: GetTopicResponse[]) {
        let mostViewedMap = topics[0];
        topics.forEach((topic) => {
            if (topic.statistics.approximate_topic_views > mostViewedMap.statistics.approximate_topic_views) {
                mostViewedMap = topic;
            }
        });
        return mostViewedMap;
    }

    private async getNumChallenges(topics: GetTopicResponse[]) {
        if (featuresInfo.challengePassportEnabled) {
             return;
        }

        let totalChallenges = 0;

        // Loop through each topic and get the number of challenges for each one
        for (const topic of topics) {
            try {
                const challenges = await getChallenges(this.amplitude_callsite, topic._id.$oid) ?? [];
                totalChallenges += challenges.length;
            } catch (error) {
                console.error(`Failed to get challenges for topic ${topic._id.$oid}`);
            }
        }

        // Set the challengesLength property to the total number of challenges
        this.challengesLength = totalChallenges;
    }

    private async getEmails() {
        const topic_id_array: string[] = this.topics.map((topic) => topic._id.$oid);
        const all_captures = await getEmailCapturesForTopics(this.amplitude_callsite, topic_id_array);

        // add in the topic name and id to each email capture
        this.emailWithTopic = [];
        for (const capture of all_captures) {
            const topic = this.topics.find((topic) => topic._id.$oid === capture.topic_id.$oid);
            if (topic) {
                this.emailWithTopic.push({
                    ...capture, capture_topic_name: topic.name,
                    capture_topic_id: capture.topic_id.$oid
                });
            }
        }

        this.flattened_emails = this.emailWithTopic.map((capture) => ({
            ...capture,
            text: capture.text,
            timestamp: capture.timestamp,
        }));
        this.flattened_emails.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    }

    private async downloadCapturedEmails() {
        getMainPage().errorToastElement.showToast('Downloading emails...');
        await this.getEmails();
        let content = 'Map Id, Map Name,Email,Title,CTA,Timestamp\n';

        for (const record of this.flattened_emails) {
            content += `${escapeCsvValue(record.capture_topic_id)},${escapeCsvValue(record.capture_topic_name)},${escapeCsvValue(record.email)},${escapeCsvValue(record.text.title ?? '')},${escapeCsvValue(record.text.cta ?? '')},${escapeCsvValue(record.timestamp)}\n`;
        }

        const filename = 'emails.csv';

        const blob = new Blob([content], {
            type: 'text/csv;charset=utf-8;'
        });

        const a = document.createElement('a');
        document.body.appendChild(a);
        a.style.display = 'none';

        const url = window.URL.createObjectURL(blob);
        a.href = url;
        a.download = filename;
        a.click();

        // Revoke the object URL to free up memory
        window.URL.revokeObjectURL(url);
    }

    private async openAnalyticsPortal() {
        this.dispatchEvent(new CustomEvent('open-full-insights'));
    }

    render() {
        return html`
            ${this.isLoading ? '' : html`
                <div class="insightsWrapper">
                    ${this.statsList.map((stat) => {
                        return html`
                            <insight-card
                                statNumber=${stat.value === 0 ? 'None' : numberFormatter(stat.value, 1)}
                                .statTopic=${stat.topic}
                                .actionString=${stat.action_string}
                                statName=${stat.name}
                                moreInfoText=${ifDefined(stat.moreInfoSubscribed)}
                                @action-click=${stat.action_function}
                                @click=${stat.moreInfoSubscribedAction}
                                .blocked=${stat.blocked}
                            >
                            </insight-card>`;
                            }
                        )}
                    ${!detectMobileViewport() && featuresInfo.advancedInsightsEnabled ? html`
                        <div class="full-insights-section" @click=${this.openAnalyticsPortal}>
                            <p> Open Cross-Map Insights Portal </p>
                            <mwc-icon>query_stats</mwc-icon>
                        </div>
                    ` : ''}
                </div>
            `}
        `;
    }

    static get styles() {
        return css`
            .insightsWrapper {
                width: 100%;
                padding-bottom: 1.5rem;
                box-sizing: border-box;
                margin: 0px auto;
                /* display: grid; */
                /* grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); */
                display: flex;
                flex-wrap: wrap;
                align-items: flex-start;
                justify-content: flex-start;
                background-color: var(--main-primary-color);
                padding: 10px;
                border-radius: 8px;
                overflow-y: auto; // DO NOT REMOVE - this allows scrolling on mobile
            }

            .filtersWrapper {
                padding-bottom: 10px;
                margin: 0px auto;
                display: flex;
                --mdc-theme-secondary: var(--main-primary-color);
                justify-content: space-between;
            }

            .filtersWrapper .filterItem {
                cursor: pointer;
                padding: 16px;
                margin-right: 0.75rem;
                font-size: 1rem;
                border-radius: 5px;
                display: flex;
                align-items: center;
                background: var(--theme-lightest-color);
                transition: 0.3s all ease;
                height: fit-content;
            }

            .filtersWrapper .filterItem:hover {
                background: var(--theme-input-border-color);
            }

            .filtersWrapper .filterItem mwc-icon {
                --mdc-icon-size: 1.25rem;
                margin-right: 0.25rem;
            }

            .createNewMap {
                box-shadow: rgb(31 27 45 / 8%) 0px 2px 2px -2px, rgb(31 27 45 / 8%) 0px 4px 12px;
                border-radius: 6px;
                padding: 1.2rem;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                height: 100%;
                box-sizing: border-box;
            }

            .createTitle {
                color: var(--theme-input-color);
                font-weight: 700;
                cursor: pointer;
            }

            .createTemplate {
                font-size: 1rem;
                font-weight: 700;
                color: var(--theme-input-color);
            }

            .addIcon {
                color: var(--main-primary-color);
                cursor: pointer;
            }

            .full-insights-section {
                display: flex;
                min-width: 23%;
                align-items: center;
                flex: 1;
                border: white solid 1px;
                width: 100%;
                border-radius: 0.75rem;
                background: #1d485f;
                border-radius: 12px;
                padding: 1rem;
                box-sizing: border-box;
                color: white;
                cursor: pointer;
            }

            .full-insights-section p {
                flex: 1 0 80%;
            }

            .full-insights-section mwc-icon {
                --mdc-icon-size: 2rem;
            }

            insight-card {
                width: 24%;
            }

            @media (min-width: 551px) {
                .desktopOnly {
                    display: block;
                }

                .addIcon {
                    --mdc-icon-size: 110px;
                }

                .mobileOnly {
                    display: none;
                }

                .createTitle {
                    font-size: 30px;
                    margin-top: 1rem;
                    margin-bottom: 2rem;
                }

                .insightsWrapper {
                    gap: 10px;
                    overflow-y: visible;
                }
            }

            @media (max-width: 550px) {
                .desktopOnly {
                    display: none;
                }

                .mobileOnly {
                    display: block;
                }


                insight-card {
                    width:48%;
                }

                .createNewMap {
                    display: none;
                }

                .mobileSearch {
                    width: 100%;
                }

                .addIcon {
                    --mdc-icon-size: 40px;
                }

                .insightsWrapper {
                    gap: 5px;
                    min-width: 100%;
                }
            }
        `;
    }
}
